import React, {
	useState,
	useRef,
	useEffect,
	useCallback,
	useMemo,
	useImperativeHandle,
} from 'react';
import useEsriPointHeatmap, {
	type PointHeatmapOverlay,
} from './features/useEsriPointHeatmap';
import { SnackbarProvider } from 'notistack';
import { useDispatch, useSelector, useStore } from 'react-redux';
import TableChartIcon from '@mui/icons-material/TableChart';
import LayersIcon from '@mui/icons-material/Layers';
import EsriPolygonControl from './features/ControlsContainer/components/PolygonControl/PolygonControl';
import EsriMapContext, { useEsriMapContext } from './EsriMapContext';
import { useEsriMap } from './useEsriMap';
import {
	installEsriTestHooks,
	uninstallEsriTestHooks,
	type EsriSelectionState,
} from './testHooks';
import { useLayers } from './features/useLayers';
import { useServiceBanners } from './features/useServiceBanners';
import { ThemeLoadingController } from '@Map/theme/ThemeLoadingController';
import MessagesSnackbarContent from '@Components/Messages/MessagesSnackbarContent';
import { useTheme } from './features/useTheme';
import { useViewport } from './features/useViewport';
import { useControls } from './features/useControls';
import { useSelection, type Selection, type GeocoderPopup } from './features/useSelection';
import {
	usePropertyPane,
	selectedAssetsToFeatureItems,
} from './features/usePropertyPane';
import { useGrid } from './features/useGrid';
import type { Asset } from './features/AssetGrid/utils/types';
import { useBasemap } from './features/useBasemap';
import { usePolygonFilter } from './features/usePolygonFilter';
import { disableMapInteractionsForView } from './features/useMapModes';
import { useAddStyles } from './useAddStyles';
import { EditorProvider } from '@Components/Editor';
import { EsriBasemapId, type ThemeConfig } from './types';
import type { ReactMapProps } from '../../wrappers/types';
import {
	MapFunction,
	MapModes,
	type FitBoundsOptions,
	type MapFocusedAsset,
	type MapSelectedAssets,
	type SelectionOptions,
} from '../types';
import { functionalityEnabled, getDisplayMode, getEditMode } from '../utils';
import { colorRangeToBands } from './utils/colorExpressions';
import {
	AttributeTypes,
	CBAttributesPerLayer,
	ColorByOptions,
	EditorModes,
	EditorState,
	LayerToggle,
} from '@Map/panel/types';
import type { MapHiddenLayers } from './layers/EsriLayerManager';
import type { ConfigLayer, ConfigLayerInternal } from '@Map/panel/types';
import { SourceType } from '@Map/services/types';
import { fallbackDefaultThemeId } from '@Map/services/ThemeDefaults';
import { mapBackgroundTypeToEsriBasemap } from './utils/basemapMapping';
import {
	applyGroupLineColorPreview,
	mergeThemeEditsIntoConfig,
	enrichIconImages,
} from './utils/mergeThemeEditsIntoConfig';
import { visibilityThemeEdit } from './utils/visibilityThemeEdit';
import { computeHiddenLeafIds } from './utils/computeHiddenLeafIds';
import { BackgroundTypes } from '@Map/panel/types';
import { BackgroundRegistry } from '@Map/panel/BackgroundRegistry';
import {
	MapContainer,
	AssetGridWrapper,
	FilterCardOverlay,
	GridToggleButton,
	GeocoderCloseIcon,
	GeocoderCloseButton,
	GeocoderAddressInfo,
	GeocoderPlaceName,
	GeocoderCoords,
} from './ReactEsriMap.styles';
import { Popup, Anchor } from '@Components/Popup';
import styles from './../styles/arcgisMap.scss';
import { ControlsContainer } from './features/ControlsContainer';
import { Search } from './features/Search';
import type { SearchApi } from './features/Search/Search';
import type { SearchEndpoint } from './features/Search/types';
import {
	getAllAssetsSchemaAction,
	getAllSystemsSchemaAction,
} from '@innovyze/lib_asset_schema_store';
import sharedStore from '../../sharedStore';
import { fitViewToGeoJSONGeometries } from './utils/fitViewToGeoJSONGeometries';
import useAddPinsToMap, {
	centersForDropPinsFromGeometries,
} from './features/useAddPinToMap';
import type EsriMap from '@arcgis/core/Map';
import ReactResizeDetector from 'react-resize-detector';
import { useHoverPopup } from './features/useHoverPopup';

const FOCUSED_ASSETS_PIN_LAYER_ID = 'focused-assets-pins';
type ContainerSize = { width: number; height: number };

/** Default selection options (minZoom, maxFeatures) used when not overridden by the consumer. */
const DEFAULT_SELECTION_OPTIONS: SelectionOptions = {
	minZoom: 14,
	maxFeatures: 70,
};

const getBackgroundFromUrl = ():
	| BackgroundTypes
	| EsriBasemapId
	| undefined => {
	if (typeof window === 'undefined') return undefined;
	const params = new URLSearchParams(window.location.search);
	const candidate = params.get('background') ?? params.get('basemap');
	if (!candidate) return undefined;
	if (Object.values(BackgroundTypes).includes(candidate as BackgroundTypes)) {
		return candidate as BackgroundTypes;
	}
	if (Object.values(EsriBasemapId).includes(candidate as EsriBasemapId)) {
		return candidate as EsriBasemapId;
	}
	return undefined;
};

// Drop-in compatible subset of ReactMapProps, extended with ESRI-specific props.
type ReactEsriMapProps = Pick<
	ReactMapProps,
	| 'position'
	| 'onBoundsChange'
	| 'onPositionChange'
	| 'selectedAssets'
	| 'onSelectedAssetsChange'
	| 'dataServices'
	| 'arcGISBasemapStylesToken'
	| 'background'
	| 'mode'
	| 'config'
	| 'layerConfig'
	| 'themeEndpoint'
	| 'onSearch'
	| 'onBackgroundChanged'
	| 'selectionOptions'
	| 'focusedAssets'
	| 'zoomToFocusedAssets'
	| 'defaultThemeId'
	| 'showMultiThemes'
	| 'defaultBounds'
	| 'fitBoundsOptions'
	| 'hoverPopup'
	| 'hoverPopupDebounce'
	| 'unitSystem'
> & {
	className?: string;
	style?: React.CSSProperties;
	children?: React.ReactNode;
	assetGrid?: React.ReactElement;
	/** Filter count card shown above the map when filters are active. Pass <FilterCard /> to enable. */
	filterCard?: React.ReactElement;
	propertyPane?: React.ReactElement;
	/**
	 * Layer panel element. The map renders a toggle button that slides the panel
	 * in from the right edge. Pass <LayerPanel config={...} arcGISBasemapStylesToken={...} />;
	 * the `open` prop is managed internally by ReactEsriMap.
	 */
	layerPanel?: React.ReactElement;
	/** Render the ArcGIS geocoder search bar at the top-left of the map. */
	searchEnabled?: boolean;
	/** Custom data-service endpoints merged with ArcGIS geocoder results. */
	searchEndpoints?: SearchEndpoint[];
	/** Id of an external EsriGeocoder container. When set, the search UI is
	 * rendered into that element instead of inside the map. */
	geocoderId?: string | null;
	/** When true, restricts geocoder results to the current map viewport. Defaults to false. */
	restrictSearchToBounds?: boolean;
	/** Theme / style configuration passed to useLayers for renderer construction. */
	theming?: ThemeConfig;
	/**
	 * Controls theme editor visibility and permissions.
	 * Mirrors the Mapbox themeEditorMode prop.
	 * Defaults to EditorModes.noEdit (editor hidden).
	 */
	themeEditorMode?: EditorModes;
	/**
	 * Legacy alias: if true and themeEditorMode is not set, activates editAll mode.
	 * Mirrors the Mapbox enableStyleEditing prop.
	 */
	enableStyleEditing?: boolean;
	/**
	 * When true, range (continuous) and heatmap attributes are included in the
	 * color-by dropdown. Mirrors Mapbox's enableCBRangeHeatmap prop.
	 */
	enableColorByAttributeRange?: boolean;
	/**
	 * Called when the user completes a polygon draw. The polygon is also
	 * dispatched to Redux via setPolygonFilter for the AssetGrid automatically.
	 */
	onPolygonComplete?: (polygon: GeoJSON.Feature<GeoJSON.Polygon>) => void;
};

type EsriMapSelectionProps = {
	onSelectedAssetsChange?: (items: MapSelectedAssets | null) => void;
	onSelectionApi?: (api: Selection) => void;
	onSelectionSnapshotChange?: (snapshot: EsriSelectionState) => void;
	selectionOptions: SelectionOptions;
	selectedAssets?: MapSelectedAssets;
	onGeocoderPopupChange?: (props: GeocoderPopup) => void;
};

/** Initializes selection (click/hover + sync to layer manager). Renders nothing. */
const EsriMapSelection: React.FC<EsriMapSelectionProps> = ({
	onSelectedAssetsChange,
	onSelectionApi,
	onSelectionSnapshotChange,
	selectionOptions,
	selectedAssets,
	onGeocoderPopupChange,
}) => {
	const selection = useSelection({ selectionOptions, selectedAssets });
	const didSetSelectionApi = useRef(false);
	const prevSelectionKey = useRef<string>('');

	useEffect(() => {
		if (!didSetSelectionApi.current) {
			onSelectionApi?.(selection);
			didSetSelectionApi.current = true;
		}
	}, [selection, onSelectionApi]);

	useEffect(() => {
		const nextKey = selection.selectedAssets
			.map(
				asset =>
					// Include layerName/sensorId/priority/createDate so post-load
					// re-enrichment re-fires this effect and the gated pane open
					// below sees the enriched selection.
					`${asset.layerId ?? ''}:${asset.id}:${asset.assetType ?? ''}:${asset.layerName ?? ''}:${asset.sensorId ?? ''}:${asset.priority ?? ''}:${asset.createDate ?? ''}`,
			)
			.sort()
			.join('|');
		if (nextKey === prevSelectionKey.current) return;
		prevSelectionKey.current = nextKey;
		const items =
			selection.selectedAssets.length > 0
				? selection.selectedAssets
				: null;
		onSelectedAssetsChange?.(items);
	}, [selection.selectedAssets, onSelectedAssetsChange]);

	useEffect(() => {
		onGeocoderPopupChange?.(selection.geocoderPopupProps);
	}, [selection.geocoderPopupProps, onGeocoderPopupChange]);

	// Test hook: pushes live selection/highlight ids up to EsriMapInner on every
	// change (unlike onSelectionApi above, which only fires once on mount) so
	// window.esriTestHooks.getSelectionState reflects the current click/hover.
	useEffect(() => {
		onSelectionSnapshotChange?.({
			selectedAssetIds: selection.selectedAssetIds,
			highlightedAssetIds: selection.highlightedAssetIds,
			selectedLayerId: selection.selectedLayerId,
		});
	}, [
		selection.selectedAssetIds,
		selection.highlightedAssetIds,
		selection.selectedLayerId,
		onSelectionSnapshotChange,
	]);

	return null;
};

/** Watches polygon filter state and highlights matching assets on the map. Renders nothing. */
const EsriMapPolygonFilter: React.FC = () => {
	usePolygonFilter();
	return null;
};

/** Pins + optional zoom for focused assets (separate pin layer from search/selection). */
const EsriMapFocusedAssets: React.FC<{
	focusedAssets?: MapFocusedAsset[];
	zoomToFocusedAssets?: boolean;
	fitBoundsOptions?: FitBoundsOptions;
}> = ({ focusedAssets, zoomToFocusedAssets, fitBoundsOptions }) => {
	const { view } = useEsriMapContext();
	const setFocusedPins = useAddPinsToMap({
		layerId: FOCUSED_ASSETS_PIN_LAYER_ID,
	});

	const serializedFocusedGeometries = focusedAssets?.length
		? JSON.stringify(focusedAssets.map(a => [a._id, a.geometry]))
		: '';

	const serializedZoomFocus =
		zoomToFocusedAssets && serializedFocusedGeometries
			? serializedFocusedGeometries
			: '';

	useEffect(() => {
		if (!serializedFocusedGeometries) {
			setFocusedPins(null);
			return;
		}
		const pairs = JSON.parse(serializedFocusedGeometries) as [
			string,
			GeoJSON.Geometry,
		][];
		const geometries = pairs
			.map(([, g]) => g)
			.filter(
				(g): g is GeoJSON.Geometry =>
					!!g && typeof g === 'object' && 'coordinates' in g,
			);
		const centers = centersForDropPinsFromGeometries(geometries);
		setFocusedPins(centers.length ? centers : null);
	}, [serializedFocusedGeometries, setFocusedPins]);

	useEffect(() => {
		if (!view || !serializedZoomFocus) return;

		const pairs = JSON.parse(serializedZoomFocus) as [
			string,
			GeoJSON.Geometry,
		][];
		const geometries = pairs
			.map(([, g]) => g)
			.filter(
				(g): g is GeoJSON.Geometry =>
					!!g && typeof g === 'object' && 'coordinates' in g,
			);
		if (!geometries.length) return;

		let cancelled = false;
		void view.when().then(() => {
			if (!cancelled) {
				fitViewToGeoJSONGeometries(view, geometries, fitBoundsOptions);
			}
		});
		return () => {
			cancelled = true;
		};
	}, [view, serializedZoomFocus]);

	return null;
};

/** Handles basemap switching when background prop changes. Renders nothing. */
const EsriMapBasemap: React.FC<{
	background?: BackgroundTypes | EsriBasemapId;
}> = ({ background }) => {
	const { setBasemap } = useBasemap();
	const { view } = useEsriMapContext();

	useEffect(() => {
		if (!view || !background) return;

		// Map the background type to ESRI basemap ID
		const esriBasemap = mapBackgroundTypeToEsriBasemap(
			background as BackgroundTypes,
		);
		setBasemap(esriBasemap);
	}, [background, view, setBasemap]);

	return null;
};

// Inner component — renders inside EsriMapContext so hooks can consume it.
const EsriMapInner: React.FC<
	Pick<
		ReactEsriMapProps,
		| 'position'
		| 'dataServices'
		| 'children'
		| 'assetGrid'
		| 'filterCard'
		| 'propertyPane'
		| 'layerPanel'
		| 'searchEnabled'
		| 'searchEndpoints'
		| 'geocoderId'
		| 'restrictSearchToBounds'
		| 'background'
		| 'layerConfig'
		| 'themeEndpoint'
		| 'selectedAssets'
		| 'onSelectedAssetsChange'
		| 'onSearch'
		| 'onBackgroundChanged'
		| 'onBoundsChange'
		| 'onPositionChange'
		| 'arcGISBasemapStylesToken'
		| 'themeEditorMode'
		| 'enableStyleEditing'
		| 'enableColorByAttributeRange'
		| 'onPolygonComplete'
		| 'selectionOptions'
		| 'focusedAssets'
		| 'zoomToFocusedAssets'
		| 'defaultThemeId'
		| 'showMultiThemes'
		| 'defaultBounds'
		| 'fitBoundsOptions'
		| 'hoverPopup'
		| 'hoverPopupDebounce'
		| 'unitSystem'
	>
> = ({
	position,
	dataServices,
	children,
	assetGrid,
	filterCard,
	propertyPane,
	layerPanel,
	searchEnabled,
	searchEndpoints,
	geocoderId,
	restrictSearchToBounds,
	background,
	layerConfig,
	themeEndpoint,
	selectedAssets,
	onSelectedAssetsChange,
	onSearch,
	onBackgroundChanged,
	onBoundsChange,
	onPositionChange,
	arcGISBasemapStylesToken,
	themeEditorMode,
	enableStyleEditing,
	enableColorByAttributeRange,
	onPolygonComplete,
	selectionOptions,
	focusedAssets,
	zoomToFocusedAssets,
	defaultThemeId,
	showMultiThemes,
	defaultBounds,
	fitBoundsOptions,
	hoverPopup,
	hoverPopupDebounce,
	unitSystem,
}) => {
	const [containerSize, setContainerSize] = useState<ContainerSize>({
		width: 0,
		height: 0,
	});
	const [geocoderPopup, setGeocoderPopup] = useState<GeocoderPopup>({});
	const searchApiRef = useRef<SearchApi | null>(null);
	const [isGridVisible, setIsGridVisible] = useState(false);
	const [isLayerPanelOpen, setIsLayerPanelOpen] = useState<boolean>(
		layerPanel?.props?.open ?? false,
	);
	const [localBackground, setLocalBackground] = useState<
		BackgroundTypes | EsriBasemapId | undefined
	>(() => background ?? getBackgroundFromUrl());
	const [panelHiddenLayers, setPanelHiddenLayers] = useState<MapHiddenLayers>(
		[],
	);
	useEffect(() => {
		if (background) {
			setLocalBackground(background);
			return;
		}
		setLocalBackground(getBackgroundFromUrl());
	}, [background]);
	const [selectionApi, setSelectionApi] = useState<Selection | null>(null);
	// Test hook: live selection/highlight ids for window.esriTestHooks.getSelectionState.
	// selectionApi (above) only snapshots once on mount, so selection changes are
	// pushed up separately via EsriMapSelection's onSelectionSnapshotChange.
	const [selectionSnapshot, setSelectionSnapshot] =
		useState<EsriSelectionState>({
			selectedAssetIds: [],
			highlightedAssetIds: [],
			selectedLayerId: null,
		});
	const [isDrawing, setIsDrawing] = useState(false);
	const suppressNextPropertyPaneOpenRef = useRef(false);

	const {
		items: propertyPaneItems,
		isOpen: propertyPaneIsOpen,
		activeIndex: propertyPaneActiveIndex,
		openPropertyPane,
		closePropertyPane,
	} = usePropertyPane();

	const handleSelectedAssetsChange = useCallback(
		(assets: MapSelectedAssets | null) => {
			if (suppressNextPropertyPaneOpenRef.current && assets?.length) {
				suppressNextPropertyPaneOpenRef.current = false;
				onSelectedAssetsChange?.(assets);
				return;
			}

			if (!assets || assets.length === 0) {
				closePropertyPane();
				selectionApi?.clearHighlight();
			} else if (assets.every(asset => !!asset.layerName)) {
				// Wait until enrichment resolves layerName; opening earlier groups
				// deep-linked selections under an "undefined" header. Map clicks
				// are already enriched, so they open immediately.
				openPropertyPane(selectedAssetsToFeatureItems(assets));
			}
			onSelectedAssetsChange?.(assets);
		},
		[openPropertyPane, closePropertyPane, onSelectedAssetsChange, selectionApi],
	);

	useEffect(() => {
		if (!propertyPaneIsOpen) {
			selectionApi?.clearHighlight();
		}
	}, [propertyPaneIsOpen, selectionApi]);

	const mapContext = useEsriMapContext();
	const banners = useServiceBanners(mapContext.view);

	const themeLoadingControllerRef = useRef<ThemeLoadingController | null>(null);
	if (!themeLoadingControllerRef.current) {
		themeLoadingControllerRef.current = new ThemeLoadingController();
	}
	const themeLoadingController = themeLoadingControllerRef.current;

	useEffect(() => {
		return themeLoadingController.subscribe(loading => {
			if (loading) banners.showThemeLoading();
			else banners.hideThemeLoading();
		});
	}, [themeLoadingController, banners]);

	// Wire data services and optional layer config → ServiceManager / EsriLayerManager.
	// When layerConfig is provided, layer array comes from ConfigAdaptor (setLayerConfig-style).
	const layers = useLayers({
		dataServices,
		layerConfig,
		themeEndpoint,
		onServiceLoading: banners.onServiceLoading,
		onServicesAllLoaded: banners.onServicesAllLoaded,
		onServiceError: banners.onServiceError,
		onMinZoom: banners.onMinZoom,
		onResetMinZoom: banners.onResetMinZoom,
		fitBoundsOptions,
		hasExternalPosition: !!position || !!defaultBounds,
		unitSystem,
	});

	// Viewport: reset/set position and fit to data extents (uses layers.fitToDataExtents).
	const viewport = useViewport({
		fitToDataExtents: layers.fitToDataExtents,
		onBoundsChange,
		onPositionChange,
		defaultBounds,
		hasExternalPosition: !!position,
		fitBoundsOptions,
	});
	const controls = useControls({ resetPosition: viewport.resetPosition });
	const grid = useGrid({ viewport, selection: selectionApi ?? undefined });

	const theme = useTheme(themeEndpoint, defaultThemeId, layers.getTileColorExpression, layers.symbolLoader, layers.refreshSymbolSprites, layers.getIconDataUrl, layers.manager, layers.isTileLookupPending, layers.ensureSymbolSprites);

	// Test hook: expose a QA canvas-automation API (click coordinates, colour
	// assertions, selection/theme/property-pane state) so Cypress specs can
	// drive/assert individual assets, which otherwise render as untargetable
	// VectorTileLayer tiles. Gated on window.Cypress; no-op in production.
	// See ./testHooks.ts.
	useEffect(() => {
		const view = mapContext.view;
		if (!view) return;
		installEsriTestHooks(view, {
			getSelectionState: () => selectionSnapshot,
			getThemeConfig: layerId => {
				const merged = {
					...theme.loadedThemeEdits[layerId],
					...theme.pendingEdits[layerId],
				};
				return Object.keys(merged).length ? merged : undefined;
			},
			getPropertyPaneState: () => ({
				isOpen: propertyPaneIsOpen,
				assets: propertyPaneItems,
				activeIndex: propertyPaneActiveIndex,
			}),
		});
		return () => uninstallEsriTestHooks();
	}, [
		mapContext.view,
		selectionSnapshot,
		theme.loadedThemeEdits,
		theme.pendingEdits,
		propertyPaneIsOpen,
		propertyPaneItems,
		propertyPaneActiveIndex,
	]);

	const hover = useHoverPopup({
		enabled: !!hoverPopup,
		debounceMs: hoverPopupDebounce,
	});

	const resolvedSelectionOptions = useMemo(
		() => ({
			...DEFAULT_SELECTION_OPTIONS,
			...selectionOptions,
		}),
		[selectionOptions],
	);

	// Keep map centering/fly-to operations aligned to the visible viewport
	// when the bottom asset grid overlay is open.
	useEffect(() => {
		const view = mapContext.view;
		if (!view) return;

		const basePadding = fitBoundsOptions?.padding;
		const normalizedPadding =
			typeof basePadding === 'number'
				? {
						top: basePadding,
						right: basePadding,
						bottom: basePadding,
						left: basePadding,
					}
				: {
						top: basePadding?.top ?? 0,
						right: basePadding?.right ?? 0,
						bottom: basePadding?.bottom ?? 0,
						left: basePadding?.left ?? 0,
					};

		const gridBottomPadding =
			isGridVisible && containerSize.height > 0
				? Math.round(containerSize.height * 0.5)
				: 0;

		view.padding = {
			...normalizedPadding,
			bottom: normalizedPadding.bottom + gridBottomPadding,
		};
	}, [
		mapContext.view,
		fitBoundsOptions?.padding,
		isGridVisible,
		containerSize.height,
	]);

	// When a full theme loads (initial restore or changeTheme), push its edits
	// into ConfigAdaptor so convertForPanel() returns the correct iconImage (SVG)
	// for each themed layer. Without this, mergeThemeEditsIntoConfig uses the
	// stale iconImage from the base config (e.g. manhole instead of other-valve).
	// configLoaded guards against the early-load race where layerConfig arrives
	// after useTheme restores saved edits — without it, editLayer silently
	// no-ops because configAdaptorRef.current is still null at that point.
	useEffect(() => {
		if (!layers.configLoaded) return;
		if (Object.keys(theme.loadedThemeEdits).length === 0) return;
		Object.entries(theme.loadedThemeEdits).forEach(([layerId, edits]) => {
			layers.editLayer(layerId, edits as ConfigLayerInternal);
		});
	}, [theme.loadedThemeEdits, layers.configLoaded]);

	// Ref so the allLayers effect always invokes the latest applyLayerTheme
	// closure without the effect itself needing to re-run on every theme update.
	const applyLayerThemeRef = useRef(theme.applyLayerTheme);
	applyLayerThemeRef.current = theme.applyLayerTheme;

	// Ref to reapplyAllPendingEdits so the LOOKUP_COMPLETE / style-loaded callback
	// always re-applies theme edits using the synchronous pendingEditsRef (not
	// React state, which can lag behind during the lookup-complete race window).
	const reapplyAllPendingEditsRef = useRef(theme.reapplyAllPendingEdits);
	reapplyAllPendingEditsRef.current = theme.reapplyAllPendingEdits;

	// Tracks the latest config-level colorBy layers so the style-loaded callback
	// can re-apply them without capturing a stale closure.
	const latestColorByLayersRef = useRef<ConfigLayerInternal[]>([]);

	const editorMode = getEditMode(themeEditorMode, enableStyleEditing);
	const editorState =
		editorMode === EditorModes.editAll ||
		(editorMode === EditorModes.editOwn && theme.isUserTheme)
			? EditorState.edit
			: EditorState.readonly;
	const showThemeSettings = editorMode === EditorModes.editAll;

	// Bridge: called by EditorProvider on every style change.
	const onLayerEdit = (layerId: string, edits: ConfigLayerInternal) => {
		// Effective type comes from PENDING (a colour-only edit carries no `type`,
		// but the layer may still be a heatmap). accumulateEdit updates the ref
		// synchronously, so read it before/after to detect bubble + transitions.
		const prevType = theme.pendingEditsRef.current.layers[layerId]?.type;
		theme.accumulateEdit(layerId, edits);
		const curType = theme.pendingEditsRef.current.layers[layerId]?.type;
		const isBubble = curType === 'bubble';

		if (isBubble) {
			// A heatmap layer is owned end-to-end by the single producer — every
			// edit (toggle on, recolour, scale, opacity) flows through here only,
			// so the diamond colour-by path can never race it and blank the icons.
			theme.setSymbolDisplayMode(layerId, 'bubble', edits.scale);
		} else if (prevType === 'bubble') {
			// Leaving heatmap mode → the producer restores the pristine diamond
			// and re-applies the colour-by on it.
			theme.setSymbolDisplayMode(layerId, 'symbol', edits.scale);
		} else {
			// Normal (non-heatmap) layer — unchanged Gap B colour-by / icon / line.
			if (edits.colorBy === 'attribute' && edits.attribute && edits.colorRange?.length) {
				theme.setAttributeColor(layerId, {
					field: edits.attribute,
					values: edits.colorRange.map(({ value, color }) => ({ value, color })),
				});
			} else if (edits.colorBy === 'range' && edits.attribute && edits.colorRange?.length) {
				theme.setColorByRange(layerId, {
					field: edits.attribute,
					breaks: colorRangeToBands(edits.colorRange),
				});
			} else if (edits.color) {
				theme.setFixedColor(layerId, edits.color);
			}

			if (edits.icon != null || edits.iconBackground != null) {
				if (edits.icon && edits.color) {
					theme.setIcon(layerId, edits.icon, edits.color, edits.iconBackground);
				}

				if (edits.iconBackground != null) {
					// VTL: toggle -nobackground on all variant style layers.
					theme.setIconBackground(layerId, edits.iconBackground);
					// GeoJSON: re-rasterise the PictureMarkerSymbol (requires icon + color).
					if (edits.icon && edits.color) {
						theme.setIcon(layerId, edits.icon, edits.color, edits.iconBackground);
					}
				}

				// Update iconImage swatch in the LayerPanel.
				if (edits.icon && edits.color) {
					const newIconImage = layers.getIconSvg(edits.icon, edits.color);
					if (newIconImage) theme.accumulateEdit(layerId, { iconImage: newIconImage });
				}
			}

			if (edits.lineWidth != null)
				theme.setStrokeWidth(layerId, edits.lineWidth);
		}

		if (edits.minZoom != null || edits.maxZoom != null) {
			theme.setVisibilityRange(layerId, edits.minZoom ?? null, edits.maxZoom ?? null);
		}
		// Match Mapbox PanelControl._onEditLayer: drives attribute value fetch,
		// validColorBy, and virtual sub-layers for per-value colors in the panel.
		layers.editLayer(layerId, edits);
		// Skip the generic re-apply for heatmap layers and bubble↔symbol
		// transitions — setSymbolDisplayMode (the single producer) already owns
		// their styling; running applyLayerTheme would re-introduce the racing
		// diamond colour-by that blanked the glow on recolour.
		if (curType !== 'bubble' && prevType !== 'bubble') {
			theme.applyLayerTheme(layerId, edits);
		}
	};

	/** Recursively flattens a ConfigLayer tree to all layers that have id+idPath. */
	function flattenLayerConfig(layers: ConfigLayer[]): ConfigLayerInternal[] {
		return layers.flatMap(layer => {
			const children = flattenLayerConfig(layer.layers ?? []);
			if (layer.id != null && layer.idPath != null) {
				return [layer as ConfigLayerInternal, ...children];
			}
			return children;
		});
	}

	// Attributes fetched asynchronously for tile-based services (those with
	// attributesUrl). Populated by the fetchAttributesList callback below.
	const [tileAttributesPerLayer, setTileAttributesPerLayer] =
		useState<CBAttributesPerLayer>({});

	/**
	 * Derives attribute field names from inline ONU (GeoJSON) data services.
	 * ConfigAdaptorLayer.showColorBy only recognises tile services with an
	 * attribute endpoint; for ONU services we build the attribute map here
	 * so the color-by editor can be populated without a server round-trip.
	 * Tile-service attributes (fetched async) are merged in via tileAttributesPerLayer.
	 */
	const attributesPerLayer = useMemo((): CBAttributesPerLayer => {
		const onuResult: CBAttributesPerLayer = {};

		if (dataServices?.length && layerConfig?.length) {
			// Step 1: scan each ONU service's feature properties to get field list.
			const serviceFieldMap: Record<string, CBAttributesPerLayer[string]> =
				{};
			for (const ds of dataServices) {
				if ((ds as any).type !== SourceType.ONU) continue;
				const features: any[] = (ds as any).data?.features ?? [];
				if (!features.length) continue;
				const fieldTypes = new Map<string, AttributeTypes>();
				for (const f of features.slice(0, 10)) {
					for (const [key, value] of Object.entries(f.properties ?? {})) {
						if (key === '_id') continue;
						if (!fieldTypes.has(key)) {
							fieldTypes.set(
								key,
								typeof value === 'number'
									? AttributeTypes.range
									: AttributeTypes.discrete,
							);
						}
					}
				}
				serviceFieldMap[ds.id] = Array.from(fieldTypes.entries()).map(
					([id, fieldType]) => ({
						id,
						fieldType,
					}),
				);
			}

			// Step 2: map layer IDs to their service's field list.
			function walkConfig(layers: ConfigLayer[]) {
				for (const layer of layers) {
					if (
						layer.id &&
						layer.dataServiceId &&
						serviceFieldMap[layer.dataServiceId]
					) {
						onuResult[layer.id] = serviceFieldMap[layer.dataServiceId];
					}
					if (layer.layers?.length) walkConfig(layer.layers);
				}
			}
			walkConfig(layerConfig);
		}

		return { ...onuResult, ...tileAttributesPerLayer };
	}, [dataServices, layerConfig, tileAttributesPerLayer]);

	/**
	 * Post-processes resolvedConfig to inject showColorBy for layers that have
	 * derived ONU attributes. ConfigAdaptorLayer.showColorBy is always undefined
	 * for ONU services so we patch it after the adaptor has resolved.
	 */
	const resolvedConfigWithColorBy = useMemo((): ConfigLayerInternal[] => {
		const base = (layers.resolvedConfig ?? []) as ConfigLayerInternal[];
		if (!Object.keys(attributesPerLayer).length) return base;

		function inject(layers: ConfigLayerInternal[]): ConfigLayerInternal[] {
			return layers.map(layer => {
				const hasAttrs = !!attributesPerLayer[layer.id]?.length;
				const patched: ConfigLayerInternal =
					hasAttrs && !layer.showColorBy
						? { ...layer, showColorBy: ColorByOptions.attribute }
						: layer;
				if (patched.layers?.length) {
					return { ...patched, layers: inject(patched.layers) };
				}
				return patched;
			});
		}
		return inject(base);
	}, [layers.resolvedConfig, attributesPerLayer]);

	// Stable reference for the LayerPanel's `config` prop. Without this memo
	// the inline computation produces a fresh array on every render, which
	// causes LayersTreeView's re-seed effect (keyed on [config, hiddenLayers])
	// to re-run and overwrite the user's local toggle state with the theme's
	// saved `visible: false` defaults.
	//
	// `layers.getIconSvg` is deliberately omitted from deps: useLayers returns
	// a fresh object literal each render so the function reference is unstable,
	// but its behavior depends entirely on `symbolLoaderRef.current` — which is
	// already tracked via `layers.symbolLoader`. Including `getIconSvg` here
	// would defeat the memo and reintroduce the original re-seed clobbering.
	const panelConfig = useMemo(
		() =>
			applyGroupLineColorPreview(
				enrichIconImages(
					mergeThemeEditsIntoConfig(
						resolvedConfigWithColorBy,
						theme.pendingEdits,
						layers.symbolLoader ?? null,
					),
					theme.pendingEdits,
					layers.getIconSvg,
				),
				theme.pendingEdits,
			),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[resolvedConfigWithColorBy, theme.pendingEdits, layers.symbolLoader],
	);

	// Push the merged config's `visible` flags into the manager whenever
	// panelConfig changes (initial mount, theme switch, config reload). Without
	// this, layers hidden by the theme's saved edits start rendered visible on
	// the map even though the panel eye icons show them as hidden — they only
	// converge after the user clicks something. User clicks themselves flow
	// through onLayerToggle below and do not re-enter this effect, since clicks
	// do not change panelConfig's identity.
	useEffect(() => {
		if (!panelConfig.length) return;
		const hiddenLeafIds = computeHiddenLeafIds(panelConfig);
		layers.manager?.setLayerVisibility(hiddenLeafIds);
		setPanelHiddenLayers(hiddenLeafIds);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [panelConfig]);

	// GeoJSON layers receive their initial renderer via VectorTileStyleToEsriRenderer,
	// which cannot produce ClassBreaksRenderer / UniqueValueRenderer from Mapbox
	// color expressions.  We watch for new layers being added to the map and
	// immediately override the renderer whenever colorBy is set in the config.
	// We also re-apply after every VectorTileLayer loadStyle completion because
	// loadStyle resets all setStyleLayer overrides — without this, color expressions
	// applied to tile-service layers are silently discarded when the style reloads.
	useEffect(() => {
		const view = mapContext.view;
		if (!view) return;

		const leafLayersWithColorBy = flattenLayerConfig(
			resolvedConfigWithColorBy,
		).filter(
			l =>
				l.colorBy === ColorByOptions.range ||
				l.colorBy === ColorByOptions.attribute,
		);

		// Keep ref up-to-date so the style-loaded callback always uses the latest list.
		latestColorByLayersRef.current = leafLayersWithColorBy;

		const applyAll = () => {
			// Config-level colorBy — skip layers where the theme is driving colorBy instead.
			for (const layer of latestColorByLayersRef.current) {
				const pending = theme.pendingEditsRef.current.layers[layer.id];
				if (pending?.colorBy) continue;
				// Bubble layers are owned by the sensor producer — never let the
				// config-level diamond colour-by touch them.
				if (pending?.type === 'bubble') continue;
				applyLayerThemeRef.current(layer.id, {
					colorBy: layer.colorBy,
					attribute: layer.attribute,
					colorRange: layer.colorRange,
				});
			}
			// Theme-level edits — re-apply all pending theme edits via the synchronous
			// pendingEditsRef (not React state) so that LOOKUP_COMPLETE callbacks always
			// see the latest colorRange even before the next React render cycle.
			reapplyAllPendingEditsRef.current();
		};

		applyAll(); // For layers already on the map at effect time
		const handle = view.map?.allLayers.on('after-add', applyAll);
		// Re-apply after every VectorTileLayer loadStyle so tile-service colorBy survives style reloads.
		layers.setOnStyleLoaded(applyAll);
		// Re-apply after attribute Lookups finish loading: tile-layer color
		// expressions for nominalDiameter etc. are id → value lookups; until
		// the lookup data arrives the expression falls back to the default
		// grey color. Rebuild as soon as it's available.
		layers.setOnLookupComplete(applyAll);
		// Bake the resolved sensor heatmap glow into the VTL style as it's built
		// (reads live pending edits) so the asset icon never flickers through on
		// style reload and the glow survives theme saves. Single producer, also
		// used by the immediate toggle path. (Reads only refs internally, so a
		// single registration stays correct across renders.)
		layers.setSensorStyleResolver(theme.resolveSensorStyleLayer);
		return () => {
			handle?.remove();
			layers.setOnStyleLoaded(null);
			layers.setOnLookupComplete(null);
			layers.setSensorStyleResolver(null);
		};
	}, [mapContext.view, resolvedConfigWithColorBy]);

	// Stable reference — EditorProvider's onSelect holds this in a memoized
	// closure; reading theme.pendingEditsRef.current.layers ensures fresh data
	// even after a theme switch without recreating the function on every render.
	const getSelectedLayer = useCallback(
		(layerId: string): ConfigLayerInternal | undefined =>
			flattenLayerConfig(
				mergeThemeEditsIntoConfig(
					resolvedConfigWithColorBy,
					theme.pendingEditsRef.current.layers,
					layers.symbolLoader,
				),
			).find(l => l.id === layerId),
		[resolvedConfigWithColorBy],
	);

	const handleReset = () => {
		flattenLayerConfig(resolvedConfigWithColorBy).forEach(layer => {
			if (layer.color) theme.setFixedColor(layer.id, layer.color);
			if (layer.lineWidth != null)
				theme.setStrokeWidth(layer.id, layer.lineWidth);
			if (layer.minZoom != null || layer.maxZoom != null) {
				theme.setVisibilityRange(
					layer.id,
					layer.minZoom ?? null,
					layer.maxZoom ?? null,
				);
			}
		});
		theme.resetTheme();
	};

	return (
		<EsriMapContext.Provider
			value={{
				...mapContext,
				layerSelectionApi: layers.layerSelectionApi,
				selectionApi,
				getLayerIdsByServiceId: layers.getLayerIdsByServiceId,
				isDrawing,
				setIsDrawing,
			}}>
			<ReactResizeDetector
				handleWidth
				handleHeight
				onResize={(width, height): void =>
					setContainerSize({
						width: width ?? 0,
						height: height ?? 0,
					})
				}
			/>
			<EsriMapBasemap background={localBackground} />
			<EsriMapFocusedAssets
				focusedAssets={focusedAssets}
				zoomToFocusedAssets={zoomToFocusedAssets}
				fitBoundsOptions={fitBoundsOptions}
			/>
			<EsriMapSelection
				onSelectedAssetsChange={handleSelectedAssetsChange}
				onSelectionApi={setSelectionApi}
				onSelectionSnapshotChange={setSelectionSnapshot}
				selectionOptions={resolvedSelectionOptions}
				selectedAssets={selectedAssets}
				onGeocoderPopupChange={setGeocoderPopup}
			/>
			<EsriMapPolygonFilter />
			{((searchEnabled ??
				functionalityEnabled(
					MapFunction.geocoderControl,
					mapContext.displayMode,
				)) ||
				geocoderId) && (
				<Search
					searchEndpoints={searchEndpoints}
					onSearch={onSearch}
					geocoderId={geocoderId}
					restrictSearchToBounds={restrictSearchToBounds}
					onSearchApi={api => {
						searchApiRef.current = api;
					}}
				/>
			)}
			{children}
			{hoverPopup &&
				React.cloneElement(hoverPopup, {
					...hover.hoverPopupPayload,
					cancelPopupUpdate: hover.cancelPopupUpdate,
					containerSize,
				})}
			{geocoderPopup.features?.length ? (
				<Popup
					features={geocoderPopup.features}
					point={geocoderPopup.point}
					anchor={Anchor.bottom}
					containerSize={containerSize}
					dataCy="geocode-popup-wrapper"
					contentDataCy="geocode-popup-content">
					{features => {
						const feature = features[0];
						const [lng, lat] = feature.center;
						const addrCenter = feature.addressCenter ?? [lng, lat];
						const coordSearch = `${lat.toFixed(6)},${lng.toFixed(6)}`;
						const coordDisplay = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
						const setSearch = (term: string) => (e: React.MouseEvent) => {
							e.preventDefault();
							searchApiRef.current?.setSearch(term);
						};
						return (
							<GeocoderAddressInfo>
								<GeocoderCloseButton
									onClick={() => {
										selectionApi?.clearGeocoderPopup();
										setGeocoderPopup({});
									}}>
									<GeocoderCloseIcon />
								</GeocoderCloseButton>
								{feature.place_name && (
									<GeocoderPlaceName data-cy="geocode-popup-place-name">
										<a
											href="#"
											onClick={setSearch(feature.place_name)}
											onMouseEnter={() =>
												selectionApi?.setPreviewPin(addrCenter as [number, number])
											}
											onMouseLeave={() =>
												selectionApi?.clearPreviewPin()
											}>
											{feature.place_name}
										</a>
									</GeocoderPlaceName>
								)}
								<GeocoderCoords data-cy="geocode-popup-lng-lat">
									<a
										href="#"
										onClick={setSearch(coordSearch)}
										onMouseEnter={() =>
											selectionApi?.setPreviewPin([lng, lat])
										}
										onMouseLeave={() =>
											selectionApi?.clearPreviewPin()
										}>
										{coordDisplay}
									</a>
								</GeocoderCoords>
							</GeocoderAddressInfo>
						);
					}}
				</Popup>
			) : null}
			{propertyPane &&
				React.cloneElement(propertyPane, {
					items:
						propertyPane.props.items ??
						(propertyPaneItems.length ? propertyPaneItems : null),
					deselectAssets:
						propertyPane.props.deselectAssets ??
						(() => {
							selectionApi?.clearSelection();
							selectionApi?.clearHighlight();
							closePropertyPane();
						}),
					highlightAssets:
						propertyPane.props.highlightAssets ??
						((assetIds: string[]) => {
							if (!assetIds.length) {
								selectionApi?.clearHighlight();
								return;
							}
							// An id can match more than one item when the
							// same id exists across asset types/layers
							// (e.g. shared by a Manhole and a Pipe). Keep
							// every match on its own layerId instead of
							// collapsing to a single layer, so hovering
							// highlights on all of them.
							const matches = propertyPaneItems.filter(i =>
								assetIds.includes(i.id),
							);
							if (!matches.length) {
								selectionApi?.clearHighlight();
								return;
							}
							selectionApi?.highlightAssetsPerLayer(
								matches.map(m => ({
									id: m.id,
									layerId: m.layerId ?? null,
								})),
							);
						}),
				})}
			<ControlsContainer
				controls={controls}
				layerPanelOpen={isLayerPanelOpen}>
				{layerPanel && (
					<GridToggleButton
						active={isLayerPanelOpen}
						onClick={() => setIsLayerPanelOpen(prev => !prev)}
						title="Toggle layer panel">
						<LayersIcon />
					</GridToggleButton>
				)}
				{assetGrid && (
					<GridToggleButton
						active={isGridVisible}
						onClick={() => setIsGridVisible(prev => !prev)}
						title="Toggle asset grid"
						data-cy="grid-toggle">
						<TableChartIcon />
					</GridToggleButton>
				)}
				{assetGrid && isGridVisible && (
					<EsriPolygonControl onPolygonComplete={onPolygonComplete} />
				)}
			</ControlsContainer>
			{filterCard && (
				<FilterCardOverlay searchEnabled={searchEnabled}>
					{filterCard}
				</FilterCardOverlay>
			)}
			{layerPanel && (
				<EditorProvider
					mode={editorMode}
					state={editorState}
					themeLoadingController={themeLoadingController}
					onLayerEdit={onLayerEdit}
					onEditorSave={() => theme.saveTheme()}
					onEditorCancel={() => theme.cancelCreating()}
					onEditorReset={handleReset}
					onCreateTheme={() => theme.createTheme()}
					onThemeEdit={name => theme.setThemeName(name)}
					onThemeChange={async themeId => {
						// Reset all renderers to base config before the incoming
						// theme's edits are applied — otherwise layers that have no
						// override in the new theme keep the previous theme's values.
						flattenLayerConfig(resolvedConfigWithColorBy).forEach(
							layer => {
								if (layer.color)
									theme.setFixedColor(layer.id, layer.color);
								if (layer.lineWidth != null)
									theme.setStrokeWidth(
										layer.id,
										layer.lineWidth,
									);
								if (layer.icon)
									theme.setIcon(layer.id, layer.icon);
								theme.setVisibilityRange(
									layer.id,
									layer.minZoom ?? null,
									layer.maxZoom ?? null,
								);
							},
						);
						// Capture which layers had icon overrides BEFORE clearing edits.
						const layersWithIconOverride = Object.entries(
							theme.pendingEdits,
						).filter(([, edits]) => edits.icon);
						// Clear ConfigAdaptor per-layer edits so resolvedConfig resets
						// to clean base values. Without this, fields like layerName and
						// icon from the old theme's unsaved edits bleed into the new
						// theme's display via configWithEdits in convertForPanel().
						layers.clearAllLayerEdits();
					// After clearing edits, ConfigAdaptor.convertForPanel() returns
					// the base (un-themed) icons. Use this to reset VTL icons for
					// layers whose icon was overridden by the old theme — if the new
					// theme doesn't override them, changeTheme won't reset them.
					// Also used as the baseline for changeTheme (resolvedConfigWithColorBy
					// is React state that still contains the old theme's edits at this point).
					const baseConfig = layers.getBaseLayerConfig();
					if (layersWithIconOverride.length > 0) {
						const baseIconById: Record<string, { icon?: string; color?: string }> = {};
						flattenLayerConfig(baseConfig).forEach(layer => {
							baseIconById[layer.id] = { icon: layer.icon, color: layer.color };
						});
						layersWithIconOverride.forEach(([layerId]) => {
							const defaults = baseIconById[layerId];
							if (defaults?.icon) {
								theme.resetLayerIcon(layerId, defaults.icon, defaults.color);
							}
						});
					}
					// Await so EditorProvider's onChangeTheme waits for pendingEdits
					// to update before calling resetSelectedLayer — otherwise the
					// Properties panel refreshes with stale data from the old theme.
					// loadedThemeEdits effect (above) pushes the new theme's edits
					// into ConfigAdaptor so convertForPanel() returns correct iconImage.
					// Use baseConfig (fetched from ConfigAdaptor after clearAllLayerEdits)
					// as the baseline — resolvedConfigWithColorBy is React state that
					// hasn't re-rendered yet and still contains the old theme's edits.
					await theme.changeTheme(themeId, flattenLayerConfig(baseConfig));
					}}
					onDeleteTheme={
						theme.isDeletable
							? () => theme.deleteTheme()
							: undefined
					}
					getSelected={getSelectedLayer}
					attributesPerLayer={attributesPerLayer}
					attributeValuesPerLayer={layers.attributeValuesPerLayer}
					fetchAttributesList={async (layerId: string) => {
						// ONU attributes are already present in attributesPerLayer (derived
						// synchronously). This handles tile-based services with attributesUrl.
						const attrs = await layers.getAttributesList(layerId);
						if (!attrs?.length) return;
						const filtered = enableColorByAttributeRange
							? attrs
							: attrs.filter(
									({ fieldType }) =>
										fieldType === AttributeTypes.discrete,
								);
						setTileAttributesPerLayer(prev => ({
							...prev,
							[layerId]: filtered,
						}));
					}}>
					{React.cloneElement(layerPanel, {
						open: isLayerPanelOpen,
						background: localBackground,
						backgroundChanged: (bg: BackgroundTypes) => {
							setLocalBackground(bg);
							onBackgroundChanged?.(
								BackgroundRegistry.getStyleWithFallback(
									bg,
									arcGISBasemapStylesToken ?? null,
								),
							);
						},
						config: panelConfig,
						themeName: theme.themeName,
						themes:
							theme.themes.length > 0 ? theme.themes : undefined,
						selectedTheme: theme.selectedThemeId,
						showMultiThemes:
							showMultiThemes ?? theme.themes.length > 0,
						showThemeSettings,
						themeSettings: theme.themeSettings,
						onThemeSettingsChange: (s: any) =>
							theme.setThemeSettings(s),
						defaultThemeId:
							defaultThemeId ?? fallbackDefaultThemeId,
						enableCBRangeHeatmap: enableColorByAttributeRange,
						isReadonlyMode: editorMode === EditorModes.noEdit,
						showBackgroundList: functionalityEnabled(
							MapFunction.backgroundControl,
							mapContext.displayMode,
						),
						onLayerToggle: ({
							layerPath,
							state,
							layerVisibility,
							hiddenLayers,
							changedLayerPaths,
						}: LayerToggle) => {
							if (layerPath && typeof state === 'boolean') {
								layers.setConfigVisibility({ layerPath, state });
								// Record every layer whose visibility value actually
								// changed as a theme edit, not just the clicked layer.
								// The panel's local updateVisibility() cascades this
								// toggle onto ancestors (turning a child on forces its
								// parent group visible) and descendants (turning a
								// group off forces every child hidden) — but that
								// cascade only lives in transient panel state. Layers
								// that were only ever hidden by inheriting "hidden"
								// from an ancestor's edit (never their own edit) lose
								// that hidden status the moment the ancestor's edit
								// flips visible again, since panelConfig's merge and
								// the leaf-visibility reconcile effect below only know
								// about explicit per-id edits, not the panel's
								// in-memory cascade. Persisting an edit for every
								// changed path — both directions — keeps pendingEdits a
								// full mirror of the panel's visibility state so
								// nothing is left to fall back on a stale ancestor
								// default.
								const pathsToPersist =
									changedLayerPaths ?? [layerPath];
								pathsToPersist.forEach(path => {
									const pathState =
										layerVisibility?.[path] ??
										(path === layerPath ? state : undefined);
									if (typeof pathState !== 'boolean') return;
									const edit = visibilityThemeEdit(path, pathState);
									if (edit) {
										theme.accumulateEdit(edit.id, edit.edit);
									}
								});
							}
							if (layerVisibility) {
								layers.syncVisibility(layerVisibility);
							}
							setPanelHiddenLayers(hiddenLayers ?? []);
						},
						hiddenLayers: panelHiddenLayers,
						unitSystem,
					})}
				</EditorProvider>
			)}
			{assetGrid && (
				<AssetGridWrapper show={isGridVisible}>
					{React.cloneElement(assetGrid, {
						onSelectedAssetsChange: (selectedAssets: Asset[]) => {
							assetGrid.props.onSelectedAssetsChange?.(
								selectedAssets,
							);
							if (selectedAssets.length > 0) {
								suppressNextPropertyPaneOpenRef.current = true;
							}
							grid.onGridRowSelect(selectedAssets);
						},
					})}
				</AssetGridWrapper>
			)}
		</EsriMapContext.Provider>
	);
};

export type ReactEsriMapRef = {
	map: EsriMap | null;
	/**
	 * Draw a density heatmap from plain points. Lives on the ref rather than as
	 * a prop because the data changes per animation frame, and because the
	 * ArcGIS objects must be built inside this bundle — see useEsriPointHeatmap.
	 */
	heatmap?: PointHeatmapOverlay;
};

const ReactEsriMap: React.ForwardRefExoticComponent<
	ReactEsriMapProps & React.RefAttributes<ReactEsriMapRef>
> = React.forwardRef<ReactEsriMapRef, ReactEsriMapProps>(
	(
		{
			arcGISBasemapStylesToken,
			config,
			background,
			position,
			className,
			style,
			dataServices,
			layerConfig,
			themeEndpoint,
			themeEditorMode,
			enableStyleEditing,
			enableColorByAttributeRange,
			children,
			assetGrid,
			filterCard,
			propertyPane,
			layerPanel,
			searchEnabled,
			searchEndpoints,
			geocoderId,
			restrictSearchToBounds,
			onSearch,
			onBackgroundChanged,
			selectedAssets,
			onSelectedAssetsChange,
			onBoundsChange,
			onPositionChange,
			mode,
			onPolygonComplete,
			selectionOptions,
			focusedAssets,
			zoomToFocusedAssets,
			defaultThemeId,
			showMultiThemes,
			defaultBounds,
			fitBoundsOptions,
			hoverPopup,
			hoverPopupDebounce,
			unitSystem,
		},
		ref,
	) => {
		// Set sharedStore so AssetRegistry can access Redux state
		const store = useStore();
		useEffect(() => {
			sharedStore.store = store;
		}, [store]);

		// Load asset schema (needed for AssetRegistry to work)
		const dispatch = useDispatch();
		useEffect(() => {
			dispatch(getAllAssetsSchemaAction());
			dispatch(getAllSystemsSchemaAction());
		}, [dispatch]);

		// Wait for asset schema to load before rendering map
		// Check the Redux state directly (not the selector result)
		const assetSchemaState = useSelector((state: any) => state.assetSchema);
		const isSchemaLoaded =
			assetSchemaState && Object.keys(assetSchemaState).length > 0;

		const apiKey =
			arcGISBasemapStylesToken ?? config?.arcGISBasemapStylesToken;
		const resolvedUnitSystem = unitSystem ?? config?.unitSystem;
		const { mapRef, view, map } = useEsriMap({
			...(apiKey ? { apiKey } : {}),
			background: background as EsriBasemapId | undefined,
			center: position?.center as [number, number] | undefined,
			zoom: position?.zoom,
		});

		const heatmap = useEsriPointHeatmap(view);

		const containerRef = useRef<HTMLDivElement>(null);

		useAddStyles(containerRef, styles);

		useEffect(() => {
			if (mode === MapModes.Static && view) {
				void disableMapInteractionsForView(view);
			}
		}, [view, mode]);

		const displayMode = getDisplayMode(mode);

		useImperativeHandle(
			ref,
			() => ({
				get map() {
					return map;
				},
				heatmap,
			}),
			[map, heatmap],
		);

		if (!isSchemaLoaded) {
			return null;
		}

		return (
			<SnackbarProvider
				domRoot={containerRef.current ?? undefined}
				maxSnack={1}
				anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
				Components={{
					success: MessagesSnackbarContent,
					error: MessagesSnackbarContent,
					warning: MessagesSnackbarContent,
					info: MessagesSnackbarContent,
					default: MessagesSnackbarContent,
				}}
				SnackbarProps={{ 'data-cy': 'snackbar' }}>
				<EsriMapContext.Provider
					value={{
						view,
						map,

						displayMode,
						layerSelectionApi: null,
						selectionApi: null,
						getLayerIdsByServiceId: () => [],
						isDrawing: false,
						setIsDrawing: () => {},
					}}>
					<MapContainer
						ref={containerRef}
						className={className}
						style={style}>
						<div
							ref={mapRef}
							style={{ width: '100%', height: '100%' }}
						/>
						<EsriMapInner
							position={position}
							dataServices={dataServices}
							layerConfig={layerConfig}
							themeEndpoint={themeEndpoint}
							themeEditorMode={themeEditorMode}
							enableStyleEditing={enableStyleEditing}
							enableColorByAttributeRange={
								enableColorByAttributeRange
							}
							assetGrid={assetGrid}
							filterCard={filterCard}
							propertyPane={propertyPane}
							layerPanel={layerPanel}
							searchEnabled={searchEnabled}
							searchEndpoints={searchEndpoints}
							geocoderId={geocoderId}
							restrictSearchToBounds={restrictSearchToBounds}
							onSearch={onSearch}
							onBackgroundChanged={onBackgroundChanged}
							onBoundsChange={onBoundsChange}
							onPositionChange={onPositionChange}
							arcGISBasemapStylesToken={arcGISBasemapStylesToken}
							background={background}
							selectedAssets={selectedAssets}
							onSelectedAssetsChange={onSelectedAssetsChange}
							onPolygonComplete={onPolygonComplete}
							selectionOptions={selectionOptions}
							focusedAssets={focusedAssets}
							zoomToFocusedAssets={zoomToFocusedAssets}
							defaultThemeId={defaultThemeId}
							showMultiThemes={showMultiThemes}
							defaultBounds={defaultBounds}
							fitBoundsOptions={fitBoundsOptions}
							hoverPopup={hoverPopup}
							hoverPopupDebounce={hoverPopupDebounce}
							unitSystem={resolvedUnitSystem}>
							{children}
						</EsriMapInner>
					</MapContainer>
				</EsriMapContext.Provider>
			</SnackbarProvider>
		);
	},
);

export default ReactEsriMap;
